import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, PawPrint, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { track } from "../lib/analytics";
import { useNoIndex } from "../lib/useNoIndex";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const AuthShell = ({ title, subtitle, children, footer }) => (
  <div className="bg-brand-cream min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10">
    <div className="max-w-md w-full bg-white rounded-3xl border border-brand-border-warm p-8 shadow-warm animate-fade-in-up">
      <div className="flex items-center gap-2 mb-6">
        <span className="w-10 h-10 rounded-full bg-brand-forest text-white flex items-center justify-center">
          <PawPrint className="w-5 h-5" />
        </span>
        <span className="font-heading text-2xl font-black text-brand-ink">Pawground</span>
      </div>
      <h1 className="font-heading text-3xl font-bold text-brand-ink tracking-tight leading-none">{title}</h1>
      <p className="text-sm text-brand-ink/60 mt-2">{subtitle}</p>
      <div className="mt-6">{children}</div>
      {footer && <div className="mt-6 text-center text-sm text-brand-ink/70">{footer}</div>}
    </div>
  </div>
);

export const Login = () => {
  useNoIndex();
  const { signin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/search";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signin(email, password);
      navigate(redirect);
    } catch (e2) {
      const msg =
        e2?.response?.data?.detail ||
        (e2?.code === "ERR_NETWORK" || !e2?.response
          ? "Couldn't reach the server. Please check your connection and try again."
          : "Login failed");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Log in to check in at parks and leave reviews."
      footer={<>New here? <Link to="/signup" data-testid="go-signup" className="text-brand-forest font-semibold hover:underline">Create an account</Link></>}
    >
      <button
        data-testid="google-login-btn"
        onClick={googleLogin}
        className="w-full bg-white border-2 border-brand-border-warm text-brand-ink font-semibold rounded-full px-5 py-3 hover:border-brand-forest hover:bg-brand-light-green transition-all active:scale-[0.98] flex items-center justify-center gap-2"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.4c-.2 1.4-1.6 4-5.4 4-3.3 0-5.9-2.7-5.9-6s2.6-6 5.9-6c1.8 0 3.1.8 3.8 1.5l2.6-2.6C16.8 3.4 14.6 2.5 12 2.5 6.8 2.5 2.5 6.8 2.5 12S6.8 21.5 12 21.5c6.9 0 9.4-4.8 9.4-7.3 0-.5-.1-.9-.1-1.3H12z"/></svg>
        Continue with Google
      </button>
      <div className="relative my-5">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-brand-border-warm"/></div>
        <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-brand-ink/50 uppercase tracking-wider">or</span></div>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ink/40" />
          <input
            data-testid="login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full pl-11 pr-4 py-3 bg-brand-cream border border-brand-border-warm rounded-full focus:outline-none focus:ring-2 focus:ring-brand-forest"
          />
        </div>
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ink/40" />
          <input
            data-testid="login-password"
            type={showPw ? "text" : "password"}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full pl-11 pr-12 py-3 bg-brand-cream border border-brand-border-warm rounded-full focus:outline-none focus:ring-2 focus:ring-brand-forest"
          />
          <button
            type="button"
            data-testid="login-password-toggle"
            aria-label={showPw ? "Hide password" : "Show password"}
            onClick={() => setShowPw((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full text-brand-ink/50 hover:text-brand-ink hover:bg-white/60 flex items-center justify-center transition-colors"
          >
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <div className="text-right -mt-1">
          <Link
            to="/forgot-password"
            data-testid="forgot-password-link"
            className="text-xs text-brand-forest font-semibold hover:underline"
          >
            Forgot password?
          </Link>
        </div>
        <button
          type="submit"
          data-testid="login-submit"
          disabled={loading}
          className="w-full bg-brand-forest text-white font-semibold rounded-full px-5 py-3 hover:bg-brand-forest-dark transition-colors active:scale-[0.98] shadow-warm disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Log in
        </button>
      </form>
    </AuthShell>
  );
};

export const Signup = () => {
  useNoIndex();
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(email, password, name);
      track("signup_completed", { method: "email" });
      navigate("/onboarding");
    } catch (e2) {
      const msg =
        e2?.response?.data?.detail ||
        (e2?.code === "ERR_NETWORK" || !e2?.response
          ? "Couldn't reach the server. Please check your connection and try again."
          : "Signup failed. Please try again.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Join the pack to check in, save favorite parks, and more."
      footer={<>Already have one? <Link to="/login" rel="nofollow" data-testid="go-login" className="text-brand-forest font-semibold hover:underline">Log in</Link></>}
    >
      <button
        data-testid="google-signup-btn"
        onClick={googleLogin}
        className="w-full bg-white border-2 border-brand-border-warm text-brand-ink font-semibold rounded-full px-5 py-3 hover:border-brand-forest hover:bg-brand-light-green transition-all active:scale-[0.98] flex items-center justify-center gap-2"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.4c-.2 1.4-1.6 4-5.4 4-3.3 0-5.9-2.7-5.9-6s2.6-6 5.9-6c1.8 0 3.1.8 3.8 1.5l2.6-2.6C16.8 3.4 14.6 2.5 12 2.5 6.8 2.5 2.5 6.8 2.5 12S6.8 21.5 12 21.5c6.9 0 9.4-4.8 9.4-7.3 0-.5-.1-.9-.1-1.3H12z"/></svg>
        Sign up with Google
      </button>
      <div className="relative my-5">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-brand-border-warm"/></div>
        <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-brand-ink/50 uppercase tracking-wider">or</span></div>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <input
          data-testid="signup-name"
          value={name}
          required
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className="w-full px-5 py-3 bg-brand-cream border border-brand-border-warm rounded-full focus:outline-none focus:ring-2 focus:ring-brand-forest"
        />
        <input
          data-testid="signup-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="w-full px-5 py-3 bg-brand-cream border border-brand-border-warm rounded-full focus:outline-none focus:ring-2 focus:ring-brand-forest"
        />
        <div className="relative">
          <input
            data-testid="signup-password"
            type={showPw ? "text" : "password"}
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (min 6 chars)"
            className="w-full pl-5 pr-12 py-3 bg-brand-cream border border-brand-border-warm rounded-full focus:outline-none focus:ring-2 focus:ring-brand-forest"
          />
          <button
            type="button"
            data-testid="signup-password-toggle"
            aria-label={showPw ? "Hide password" : "Show password"}
            onClick={() => setShowPw((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full text-brand-ink/50 hover:text-brand-ink hover:bg-white/60 flex items-center justify-center transition-colors"
          >
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <button
          type="submit"
          data-testid="signup-submit"
          disabled={loading}
          className="w-full bg-brand-forest text-white font-semibold rounded-full px-5 py-3 hover:bg-brand-forest-dark transition-colors active:scale-[0.98] shadow-warm disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Create account
        </button>
        <p className="text-xs text-center text-brand-ink/60" data-testid="signup-legal-notice">
          By signing up, you agree to our{" "}
          <Link to="/terms" data-testid="signup-terms-link" className="font-semibold text-brand-forest underline underline-offset-2">Terms</Link>
          {" "}and{" "}
          <Link to="/privacy" data-testid="signup-privacy-link" className="font-semibold text-brand-forest underline underline-offset-2">Privacy Policy</Link>.
        </p>
      </form>
    </AuthShell>
  );
};
